import { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Link, Radio, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

import apiClient from "../../api-client";
import { AlertNote } from "../../components/AlertNote";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { CustomSelector } from "../../components/CustomSelector";
import { CustomTextField } from "../../components/CustomTextField";
import { useAuth } from "../../components/contexts/AuthContext";
import { FormField } from "../../components/FormField";
import { colors } from "../../theme/theme";
import { ONBOARDING_KEY } from "../../Tutorials/welcomeProgress.js";
import { isExternalAgency } from "@/utils/agencyFeatures";
import {
  SECTOR_OPTIONS,
  SETUP_OPTIONS,
  normalizeUrlWithHttps,
  validateUrlFormat,
} from "../Clients/clientFormOptions.js";
import { AnalyticsStep } from "../Clients/seoSetup/steps/AnalyticsStep";
import markezyLogo from "../../../markezy-login-logo.png";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;
const pop = keyframes`
  0% { opacity: 0; transform: scale(.4); }
  70% { opacity: 1; transform: scale(1.08); }
  100% { transform: scale(1); }
`;
const drift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, -20px) scale(1.06); }
`;

const AGENCY = "agency";
const OWNER = "owner";
const CLIENT = "client";
const SETUP = "setup";
const GOOGLE = "google";
const DONE_DELAY_MS = 1800;

const errorText = (requestError, fallback) => {
  const data = requestError.response?.data;
  // Un 500 devuelve HTML: solo se leen errores de campo si la respuesta es JSON
  const first = typeof data === "object" && data ? data.detail || Object.values(data).flat()[0] : null;
  return first || fallback;
};

/**
 * Primera pantalla tras verificar el email, sin la app detrás: agencia, nombre del
 * usuario, primer cliente y sus conexiones de Google, de una pregunta en una.
 * ProtectedRoute redirige aquí mientras `profile_pending`; si se cierra con el perfil ya
 * guardado, el cliente lo vuelve a pedir la bienvenida del dashboard (CommercialTour).
 */
export function Welcome() {
  const { user, isLoading, applyProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState(AGENCY);
  const [form, setForm] = useState({
    agency_name: "",
    first_name: "",
    last_name: "",
    client_name: "",
    sector: "",
    website: "",
    use_setup: true,
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [client, setClient] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);

  useEffect(() => {
    if (!done) return undefined;
    const timer = setTimeout(() => navigate("/dashboard", { replace: true }), DONE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [done, navigate]);

  if (isLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const inClientStages = [CLIENT, SETUP, GOOGLE].includes(stage);
  if (!user.profile_pending && !inClientStages && !done) return <Navigate to="/dashboard" replace />;

  const stages = [AGENCY, OWNER, CLIENT, ...(isExternalAgency(user) ? [SETUP] : []), GOOGLE];
  const stageIndex = stages.indexOf(stage);

  const update = (key, value) => {
    setError("");
    setForm((current) => ({ ...current, [key]: value }));
  };

  const goTo = (nextStage) => {
    setError("");
    setStage(nextStage);
  };

  const saveProfile = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return setError("Escribe tu nombre y apellidos.");
    setSaving(true);
    try {
      const { data } = await apiClient.post("auth/complete-profile/", {
        agency_name: form.agency_name,
        first_name: form.first_name,
        last_name: form.last_name,
      });
      setStage(CLIENT);
      applyProfile(data);
    } catch (requestError) {
      setError(errorText(requestError, "No se pudieron guardar tus datos."));
    } finally {
      setSaving(false);
    }
  };

  const validateClient = () => {
    if (!form.client_name.trim()) return "Escribe el nombre de tu cliente.";
    return validateUrlFormat(form.website);
  };

  const createClient = async () => {
    setSaving(true);
    try {
      const { data: created } = await apiClient.post("clients/", {
        name: form.client_name.trim(),
        sector: form.sector,
        website: normalizeUrlWithHttps(form.website),
        agency: user.agencyId,
        onboarding_status: "pending",
        contract_start_date: new Date().toISOString().split("T")[0],
        ...(isExternalAgency(user) && { setup_not_needed: !form.use_setup }),
      });
      // El dashboard ofrece el setup SEO/GEO de este cliente al terminar el recorrido, sin volver a ofrecer Google
      const { data: profile } = await apiClient.patch("users/me/", {
        ui_preferences: {
          ...user.ui_preferences,
          [ONBOARDING_KEY]: {
            ...user.ui_preferences[ONBOARDING_KEY],
            welcome_client: { id: created.id, name: created.name },
            google_offered_at: new Date().toISOString(),
          },
        },
      });
      setClient(created);
      setStage(GOOGLE);
      applyProfile(profile);
    } catch (requestError) {
      setError(errorText(requestError, "No se ha podido crear el cliente. Inténtalo de nuevo."));
    } finally {
      setSaving(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (stage === AGENCY) {
      if (!form.agency_name.trim()) return setError("Escribe el nombre de tu agencia para continuar.");
      return goTo(OWNER);
    }
    if (stage === OWNER) return saveProfile();
    if (stage === CLIENT) {
      const clientError = validateClient();
      if (clientError) return setError(clientError);
      return stages.includes(SETUP) ? goTo(SETUP) : createClient();
    }
    if (stage === SETUP) return createClient();
    return setDone(true);
  };

  const field = (key, label, placeholder, autoFocus = false) => (
    <FormField label={label} htmlFor={`welcome-${key}`}>
      <CustomTextField
        autoFocus={autoFocus}
        id={`welcome-${key}`}
        value={form[key]}
        onChange={(e) => update(key, e.target.value)}
        placeholder={placeholder}
        disabled={saving}
      />
    </FormField>
  );

  const agencyName = form.agency_name.trim();
  const copy = {
    [AGENCY]: {
      badge: "Bienvenido a Markezy",
      title: "¿Cómo se llama tu agencia?",
      text: "Tu prueba de 15 días ya está en marcha. Antes de entrar, ponle nombre a tu espacio.",
    },
    [OWNER]: {
      badge: `Encantados, ${agencyName}`,
      title: "¿Y tú cómo te llamas?",
      text: "Así te verán tus clientes y tu equipo dentro de Markezy.",
    },
    [CLIENT]: {
      badge: `Hola, ${form.first_name.trim() || user.first_name}`,
      title: "¿Cuál es tu primer cliente?",
      text: "Crearemos su espacio de trabajo para que empieces con trabajo real desde el primer minuto.",
    },
    [SETUP]: {
      badge: form.client_name.trim(),
      title: "¿Usamos el Setup recomendado?",
      text: "Es la puesta en marcha del cliente: análisis, web, SEO, redes sociales y campañas organizadas por fases. Las fases que no apliquen se pueden marcar como no necesarias.",
    },
    [GOOGLE]: {
      badge: "Cliente creado",
      title: `Conecta Google para ${form.client_name.trim()}`,
      text: `Vincula Google Analytics 4 y Search Console de ${form.client_name.trim()} para ver su tráfico, sus clics y sus búsquedas dentro de Markezy. Es opcional.`,
    },
  }[stage];

  const fields = {
    [AGENCY]: field("agency_name", "Nombre de la agencia", "Ej. Agencia Norte", true),
    [OWNER]: (
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
        {field("first_name", "Nombre", "", true)}
        {field("last_name", "Apellidos", "")}
      </Box>
    ),
    [CLIENT]: (
      <Box sx={{ display: "grid", gap: 2 }}>
        {field("client_name", "Nombre de la empresa", "Ej. Restaurante La Buena Mesa", true)}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <FormField label="Sector">
            <CustomSelector
              value={form.sector}
              onChange={(value) => update("sector", value)}
              options={SECTOR_OPTIONS}
              placeholder="Elige o escribe un sector"
              searchable
              freeSolo
            />
          </FormField>
          {field("website", "Página web", "miweb.com")}
        </Box>
      </Box>
    ),
    [SETUP]: (
      <Box role="radiogroup" sx={{ display: "grid", gap: 1.25 }}>
        {SETUP_OPTIONS.map((option) => {
          const selected = form.use_setup === option.value;
          return (
            <Box
              key={option.title}
              onClick={() => update("use_setup", option.value)}
              sx={{
                display: "flex",
                gap: 0.5,
                p: 1.25,
                pr: 2,
                cursor: "pointer",
                borderRadius: "12px",
                border: "1px solid",
                borderColor: selected ? "primary.main" : "divider",
                backgroundColor: "#fff",
                transition: "border-color .2s, box-shadow .2s",
                boxShadow: selected ? "0 6px 18px rgba(230, 0, 126, .08)" : "none",
              }}
            >
              <Radio size="small" checked={selected} />
              <Box sx={{ pt: "6px" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{option.title}</Typography>
                <Typography variant="body2" color="text.secondary">{option.description}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    ),
    [GOOGLE]: client && (
      <Box sx={{ display: "grid", gap: 2 }}>
        <AlertNote variant="warning">
          Google todavía está revisando Markezy, así que al autorizar puede mostrarte un aviso de aplicación no verificada.
          Pulsa «Configuración avanzada» y después «Ir a Markezy» para continuar. La conexión es segura.
        </AlertNote>
        <AnalyticsStep hideIntro clientId={client.id} client={client} onConnected={() => setGoogleConnected(true)} />
      </Box>
    ),
  }[stage];

  const createsClient = stage === SETUP || (stage === CLIENT && !stages.includes(SETUP));
  let submitLabel = "Continuar";
  if (saving) submitLabel = "Guardando…";
  else if (createsClient) submitLabel = "Crear cliente";
  else if (stage === GOOGLE) submitLabel = googleConnected ? "Entrar en Markezy" : "Ahora no, entrar";

  return (
    // En escritorio body y #root son overflow:hidden (index.css)
    <Box sx={{ height: "100vh", overflowY: "auto", position: "relative", backgroundColor: "#fff" }}>
      <Box aria-hidden sx={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        <Box sx={{ position: "absolute", width: 520, height: 520, top: -160, left: -140, borderRadius: "50%", background: "radial-gradient(circle, #fde3f0 0, transparent 65%)", animation: `${drift} 14s ease-in-out infinite` }} />
        <Box sx={{ position: "absolute", width: 460, height: 460, bottom: -180, right: -120, borderRadius: "50%", background: "radial-gradient(circle, #feecd8 0, transparent 65%)", animation: `${drift} 18s ease-in-out infinite reverse` }} />
      </Box>

      <Box component="header" sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", px: { xs: 2.25, sm: 5 }, height: { xs: 72, sm: 88 } }}>
        <Box component="img" src={markezyLogo} alt="Markezy — Tu agencia IA Ready. En un solo lugar" sx={{ height: { xs: 36, sm: 44 }, width: "auto" }} />
        {!done && (
          <Link component="button" type="button" onClick={logout} underline="hover" sx={{ fontSize: 13, color: "#697286" }}>
            Cerrar sesión
          </Link>
        )}
      </Box>

      <Box component="main" sx={{ position: "relative", minHeight: { xs: "calc(100vh - 72px)", sm: "calc(100vh - 88px)" }, display: "grid", placeItems: "center", px: 2.25, pb: 8 }}>
        {done ? (
          <Box sx={{ textAlign: "center" }}>
            <Box sx={{ width: 76, height: 76, mx: "auto", borderRadius: "50%", backgroundImage: colors.loginGradient, color: "#fff", display: "grid", placeItems: "center", animation: `${pop} .55s cubic-bezier(.2,.9,.3,1.2) both` }}>
              <Check size={38} strokeWidth={2.6} />
            </Box>
            <Typography component="h1" sx={{ mt: 3, fontSize: { xs: 28, sm: 36 }, fontWeight: 800, letterSpacing: "-1px", color: "#171b2b", animation: `${fadeUp} .5s .15s ease-out both` }}>
              Todo listo, {form.first_name.trim()}
            </Typography>
            <Typography sx={{ mt: 1, color: "#697286", animation: `${fadeUp} .5s .3s ease-out both` }}>
              Preparando el espacio de {form.client_name.trim()} en {agencyName}…
            </Typography>
            <CircularProgress size={22} sx={{ mt: 3, animation: `${fadeUp} .5s .45s ease-out both` }} />
          </Box>
        ) : (
          <Box key={stage} component="form" onSubmit={submit} sx={{ width: "min(100%, 560px)", animation: `${fadeUp} .45s ease-out both` }}>
            <Badge color="active">{copy.badge}</Badge>
            <Typography component="h1" sx={{ mt: 2, mb: 1, fontSize: { xs: 30, sm: 40 }, lineHeight: 1.1, fontWeight: 800, letterSpacing: { xs: "-1px", sm: "-1.6px" }, color: "#171b2b", overflowWrap: "anywhere" }}>
              {copy.title}
            </Typography>
            <Typography sx={{ mb: 3.5, fontSize: 15, color: "#697286", lineHeight: 1.6 }}>{copy.text}</Typography>

            {fields}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{String(error)}</Alert>}

            <Box sx={{ mt: 3.5, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 0.75 }} aria-label={`Paso ${stageIndex + 1} de ${stages.length}`}>
                {stages.map((name, index) => (
                  <Box key={name} sx={{ height: 6, width: index === stageIndex ? 26 : 6, borderRadius: 3, backgroundColor: index <= stageIndex ? "primary.main" : "#e7e9ef", transition: "all .3s" }} />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                {/* El perfil ya guardado no se edita desde aquí: solo se vuelve atrás dentro de cada bloque */}
                {(stage === OWNER || stage === SETUP) && (
                  <Button type="button" variant="ghost" size="lg" onClick={() => goTo(stage === OWNER ? AGENCY : CLIENT)} disabled={saving}>
                    <ArrowLeft size={16} /> Atrás
                  </Button>
                )}
                <Button type="submit" size="lg" disabled={saving} autoFocus={stage === SETUP}>
                  {submitLabel} <ArrowRight size={16} />
                </Button>
              </Box>
            </Box>
            {stage !== GOOGLE && <Typography sx={{ mt: 2, fontSize: 12, color: "#a0a7b5" }}>Pulsa Enter para continuar</Typography>}
          </Box>
        )}
      </Box>
    </Box>
  );
}
